/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import {
  CLINIC_USER_ROLES,
  InviteClinicUserSchema,
  type InviteClinicUserInput,
} from '@therapy/shared-validators';
import { Send } from 'lucide-react';
import { useCallback, useState, type SyntheticEvent, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from '../../components/form/form-field.js';
import { Button } from '../../components/ui/button.js';
import { type ClinicUserInvite } from '../../lib/api/clinic-users.js';
import { useInviteClinicUser } from '../../lib/queries/clinic-users.js';

import { InviteResult } from './invite-parent-form.js';

const LABEL_CLASS = 'mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300';
const SELECT_CLASS =
  'focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';

/** Invite-a-staff-member form (Admin → Users, P1-10). */
export function InviteUserForm({ onDone }: { readonly onDone: () => void }): JSX.Element {
  const { t } = useTranslation();
  const invite = useInviteClinicUser();
  const [result, setResult] = useState<ClinicUserInvite | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<InviteClinicUserInput>({
    resolver: zodResolver(InviteClinicUserSchema),
    defaultValues: { fullName: '', email: '', role: 'THERAPIST' },
  });

  const submit = useCallback(
    async (values: InviteClinicUserInput): Promise<void> => {
      setResult(await invite.mutateAsync(values));
    },
    [invite],
  );
  const onFormSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement>): void => {
      void handleSubmit(submit)(event);
    },
    [handleSubmit, submit],
  );

  if (result !== null) {
    return <InviteResult invite={result} onDone={onDone} />;
  }

  return (
    <form onSubmit={onFormSubmit} className="space-y-4" noValidate>
      <FormField
        id="user-name"
        type="text"
        label={t('admin.users.name')}
        autoComplete="name"
        error={errors.fullName?.message}
        registration={register('fullName')}
      />
      <FormField
        id="user-email"
        type="email"
        label={t('admin.users.email')}
        autoComplete="email"
        error={errors.email?.message}
        registration={register('email')}
      />
      <div>
        <label htmlFor="user-role" className={LABEL_CLASS}>
          {t('admin.users.role')}
        </label>
        <select id="user-role" className={SELECT_CLASS} {...register('role')}>
          {CLINIC_USER_ROLES.map((role) => (
            <option key={role} value={role}>
              {t(`admin.users.role_${role}`)}
            </option>
          ))}
        </select>
      </div>
      {invite.isError && (
        <p role="alert" className="text-sm text-red-600">
          {t('admin.users.inviteError')}
        </p>
      )}
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={isSubmitting || invite.isPending}>
          <Send className="h-4 w-4" aria-hidden="true" />
          {t('admin.users.invite')}
        </Button>
      </div>
    </form>
  );
}
